import React from "react";
import { Card, CardBody, CardFooter, Button, Link, addToast } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import api from "../services/api";
import { useFeaturedNews } from "../hooks/use-news";
import NewsletterSection from "./newsletter-section";

interface NewsItem {
  id: number;
  slug?: string;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  category: string;
}

interface NewsSectionProps {
  onViewAllClick: () => void;
  onNewsItemClick: (newsId: string | number) => void;
}

export const NewsSection: React.FC<NewsSectionProps> = ({ onViewAllClick, onNewsItemClick }) => {
  // Replace static mockNewsItems with API call via hook
  const { featuredNews, isLoading, error, isApiError } = useFeaturedNews(3);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Show error state if API failed and we're using fallback data
  const showApiError = error || isApiError;

  // Determine layout variants based on number of featured items
  const count = Array.isArray(featuredNews) ? featuredNews.length : 0;
  const isSingle = count === 1;
  const isDouble = count === 2;

  return (
    <section className="py-24 bg-content2" id="news">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-light mb-4">Новости и <span className="font-medium">события</span></h2>
            <p className="text-foreground/70 max-w-2xl">
              Следите за нашими новостями, специальными предложениями и мероприятиями
            </p>
            
            {/* Show API error message if needed */}
            {showApiError && (
              <div className="mt-2 text-warning text-sm flex items-center gap-1">
                <Icon icon="lucide:alert-circle" width={16} />
                <span>Данные загружены из кэша</span>
              </div>
            )}
          </div>
          <Link 
            href="#" 
            className="text-primary flex items-center gap-2 mt-4 md:mt-0 hover:underline"
            onClick={onViewAllClick}
          >
            <span>Все новости</span>
            <Icon icon="lucide:arrow-right" width={16} />
          </Link>
        </div>

        {isLoading ? (
          // Loading state (use neutral grid while loading)
          <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {[1, 2, 3].map((item) => (
              <Card key={item} className="border border-divider bg-background h-full">
                <CardBody className="p-0 overflow-hidden">
                  <div className="h-48 bg-content2 animate-pulse"></div>
                  <div className="p-6">
                    <div className="h-3 w-20 bg-content3 rounded animate-pulse mb-4"></div>
                    <div className="h-5 bg-content3 rounded animate-pulse mb-3 w-3/4"></div>
                    <div className="h-4 bg-content3 rounded animate-pulse mb-2 w-full"></div>
                    <div className="h-4 bg-content3 rounded animate-pulse mb-4 w-2/3"></div>
                    <div className="h-4 bg-content3 rounded animate-pulse w-1/4 mt-6"></div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        ) : (
          <motion.div 
            className={isSingle ? 'flex justify-center' : 'grid gap-8'}
            style={isSingle ? undefined : (isDouble ? { gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', maxWidth: 760, width: '100%', margin: '0 auto' } : { gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' })}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {featuredNews.map((item: NewsItem) => (
              <motion.div key={item.id} variants={itemVariants}>
                <Card 
                  className={`border border-divider bg-background product-card-hover ${isSingle ? 'max-w-2xl w-full' : (isDouble ? 'w-full' : 'w-full h-full')}`} 
                  isPressable
                  onPress={() => onNewsItemClick(item.slug ?? item.id)}
                  disableRipple
                >
                  <CardBody className="p-0 overflow-hidden">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={item.image} 
                        alt={item.title} 
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      <div className="absolute top-4 left-4 bg-background/80 backdrop-blur-sm px-3 py-1">
                        <p className="text-xs font-medium">{item.category}</p>
                      </div>
                    </div>
                  </CardBody>
                  <CardFooter className="flex flex-col items-start p-6">
                    <p className="text-xs text-foreground/60 mb-2">{item.date}</p>
                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                    <p className="text-foreground/70 text-sm mb-4">{item.excerpt}</p>
                    
                    <Link 
                      className="text-primary hover:text-primary-600 hover:underline flex items-center gap-1 transition-all duration-200"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onNewsItemClick(item.slug ?? item.id);
                      }}
                    >
                      Читать далее <Icon icon="lucide:arrow-right" width={16} />
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        <div className="mt-20">
          <NewsletterSection />
        </div>
      </div>
    </section>
  );
};